import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import CassetteLoader from '../components/CassetteLoader';
import { SketchPageShell, SketchBox, SketchButton } from '../components/sketchy';
import { preserveUtmParams, removeParamsPreservingUtm } from '../utils/utm';
import {
  consumeStorageReturnPath,
  readMigrationWizardState,
  writeMigrationWizardState,
  STORAGE_CALLBACK_HANDLING_KEY,
  storageSettingsSectionPath,
  type StorageProvider,
  PROVIDER_LABELS,
} from '../services/storageService';

const StorageCallback: React.FC = () => {
  const { storageLinked, loading, storageProvider, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [failedProvider, setFailedProvider] = useState<StorageProvider | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const storageError = params.get('storage_error') ?? params.get('gdrive_error');
    if (storageError) {
      setError(decodeURIComponent(storageError));
      const provider = (params.get('provider') ?? 'gdrive') as StorageProvider;
      setFailedProvider(provider);
      removeParamsPreservingUtm(['storage_error', 'gdrive_error', 'provider']);
      return;
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('storage_error') || params.get('gdrive_error')) {
      return;
    }
    if (params.get('storage_success') === 'true' || params.get('gdrive_success') === 'true') {
      return;
    }
    if (sessionStorage.getItem(STORAGE_CALLBACK_HANDLING_KEY)) {
      return;
    }
    if (error || loading) return;

    if (!user) {
      navigate(preserveUtmParams('/auth/login'), { replace: true });
      return;
    }

    if (!storageLinked) {
      const watchdog = window.setTimeout(() => {
        navigate(preserveUtmParams(consumeStorageReturnPath(storageSettingsSectionPath())), { replace: true });
      }, 12000);
      return () => window.clearTimeout(watchdog);
    }

    const isMigration = params.get('migration') === 'true';

    if (isMigration) {
      const persisted = readMigrationWizardState();
      const targetFromUrl = params.get('provider') as StorageProvider | null;
      const target = persisted?.target ?? targetFromUrl;
      const source = persisted?.source ?? (storageProvider || 'gdrive') as StorageProvider;
      if (target) {
        writeMigrationWizardState({
          step: 'confirm',
          source,
          target,
        });
      }
      navigate(preserveUtmParams(storageSettingsSectionPath()), { replace: true });
    }
  }, [storageLinked, loading, navigate, error, storageProvider, user]);

  if (error) {
    const provider = failedProvider ?? 'gdrive';
    const label = PROVIDER_LABELS[provider] ?? provider;
    return (
      <SketchPageShell className="min-h-screen flex flex-col items-center justify-center px-4">
        <SketchBox className="p-6 max-w-md w-full text-center">
          <div className="text-xl font-bold mb-4 text-red-500">
            {t('storage.callback.connectionFailed', { provider: label, defaultValue: `Could not connect ${label}` })}
          </div>
          <div className="text-sm text-[var(--color-muted)] mb-6">{error}</div>
          <SketchButton
            variant="primary"
            onClick={() => {
              navigate(preserveUtmParams(storageSettingsSectionPath()), { replace: true });
            }}
            className="w-full px-6 py-3"
          >
            {t('storage.callback.tryAgain', 'Try again')}
          </SketchButton>
        </SketchBox>
      </SketchPageShell>
    );
  }

  return (
    <SketchPageShell className="min-h-screen flex items-center justify-center">
      <CassetteLoader size={80} variant="overlay" />
    </SketchPageShell>
  );
};

export default StorageCallback;
